import Resource from 'services/api/Resource';
import { getResourceName } from 'services/api/helper';

const alpha = '/v2alpha';

/* @ngInject */
export default class RulesApiService {
    constructor($http, ProjectManager) {
        this.http = $http;
        this.ProjectManager = ProjectManager;

        /** @type {Resource} */
        this.ApiService = new Resource($http, alpha);
    }

    /**
     * @private
     * @param {string?} ruleId
     * @return {string}
     */
    resourceName(ruleId) {
        const ruleSpecifier = ['project.rules'];
        if (ruleId) {
            ruleSpecifier.push(ruleId);
        }
        return getResourceName(
            ['projects', this.ProjectManager.currentProjectId],
            ruleSpecifier
        );
    }

    getProjectRule(projectId, ruleId) {
        const ruleSpecifier = ['project.rules'];
        if (ruleId) {
            ruleSpecifier.push(ruleId);
        }
        const resourceName = getResourceName(
            ['projects', projectId],
            ruleSpecifier
        );
        return this.ApiService.get(resourceName);
    }

    organizationResourceName(organizationId, ruleId) {
        const resourceName = `organizations/${organizationId}/rules`;
        if (ruleId) {
            return `${resourceName}/${ruleId}`;
        }
        return resourceName;
    }

    rules(params) {
        const resourceName = this.resourceName();
        return this.ApiService.list(resourceName, params, 'rules');
    }

    alerts(params) {
        return this.ApiService.list("projects/-/alerts", params, 'alerts');
    }

    aggregatedAlerts(params) {
        return this.ApiService.listWithMultipleKeys(`projects/-/alerts:aggregate`, params, ['projects', 'totals']);
    }

    acknowledgeAlert(alert, comment = '') {
        const resourceName = `${alert.name}:acknowledge`;

        return this.ApiService.create(resourceName, {
            comment: comment,
        });
    }

    addComment(alert, comment) {
        const resourceName = `${alert.name}:comment`;
        return this.ApiService.create(resourceName, {
            comment: comment,
        });
    }

    addCorrectiveAction(alert, correctiveAction) {
        const resourceName = `${alert.name}:correctiveAction`;
        return this.ApiService.create(resourceName, {
            comment: correctiveAction,
        });
    }

    updateAlert(alertOrName, data, updateMask) {
        const resourceName = typeof alertOrName === 'string' ? alertOrName : alertOrName?.name;
        if (!resourceName) {
            return Promise.reject(new Error('Unable to update alert without resource name'));
        }
        return this.ApiService.update(resourceName, data, updateMask);
    }

    archiveAlert(alert, comment = '') {
        const resourceName = `${alert.name}:archive`;
        return this.ApiService.create(resourceName, {
            comment: comment
        });
    }

    getAlert(alertName) {
        return this.ApiService.get(alertName);
    }

    rule(ruleId) {
        return this.ApiService.get(this.resourceName(ruleId));
    }

    deleteRule(ruleId) {
        const resourceName = getResourceName(
            ['projects', this.ProjectManager.currentProjectId],
            ['project.rules', ruleId]
        );
        return this.ApiService.remove(resourceName);
    }

    createRule(rule) {
        return this.ApiService.create(this.resourceName(), rule);
    }

    /**
     * @param {Rule} rule
     */
    updateRule(rule) {
        const resourceName = this.resourceName(rule.id);
        return this.ApiService.put(resourceName, rule);
    }

    listTriggerStatus(id) {
        const resourceName = this.resourceName(id);
        const url = getResourceName(resourceName, ['project.rule.devices']);
        return this.ApiService.list(url, {}, 'devices');
    }

    organizationRules(organizationId, params) {
        const resourceName = this.organizationResourceName(organizationId);
        return this.ApiService.list(resourceName, params, 'rules');
    }

    getOrganizationRule(organizationId, ruleId) {
        const resourceName = this.organizationResourceName(organizationId, ruleId);
        return this.ApiService.get(resourceName);
    }

    // Create organization rule (alert template)
    createOrganizationRule(organizationId, rule) {
        const resourceName = this.organizationResourceName(organizationId);
        return this.ApiService.create(resourceName, rule);
    }

    // Update organization rule (alert template)
    updateOrganizationRule(rule) {
        // rule.name should be in format: organizations/{orgId}/rules/{ruleId}
        return this.ApiService.put(rule.name, rule);
    }

    // Delete organization rule (alert template)
    deleteOrganizationRule(ruleName) {
        return this.ApiService.remove(ruleName);
    }

    // Validate project rules
    validateProject(projectId) {
        const resourceName = `projects/${projectId}/rules:validate`;
        return this.ApiService.create(resourceName, {});
    }

    // Escalation Path CRUD
    listEscalationPaths(organizationId, params) {
        const resourceName = `organizations/${organizationId}/escalationPaths`;
        return this.ApiService.list(resourceName, params, 'escalationPaths');
    }

    getEscalationPath(name) {
        return this.ApiService.get(name);
    }

    createEscalationPath(organizationId, escalationPath) {
        const resourceName = `organizations/${organizationId}/escalationPaths`;
        return this.ApiService.create(resourceName, escalationPath);
    }

    updateEscalationPath(escalationPath) {
        return this.ApiService.put(escalationPath.name, escalationPath);
    }

    deleteEscalationPath(name) {
        return this.ApiService.remove(name);
    }
}
